import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Select from 'react-select';
import { Card, Typography, Button, Chip } from "@material-tailwind/react";
import { FaPlus, FaCheck, FaTimes, FaCar, FaMoneyCheckAlt, FaCalendarAlt, FaFileAlt, FaTag, FaFilter, FaUsers } from 'react-icons/fa';
import { useAuth } from './AuthContext';
import { rootUrl } from '../../config/config';
import Alerty from '../common/Alerty';

const BulkPosting = () => {
  const { user } = useAuth();

  // State for form data
  const [formData, setFormData] = useState({
    car_trans_type: '',
    date: new Date().toISOString().split('T')[0],
    amount: '',
    notes: '',
    your_ref: '',
    input_by: user?.username || ''
  });

  // State for car selection
  const [carPlates, setCarPlates] = useState([]);
  const [selectedCars, setSelectedCars] = useState([]);
  const [types, setTypes] = useState([]);
  const [carStatuses, setCarStatuses] = useState([]);
  const [selectedStatus, setSelectedStatus] = useState('');
  const [carsByStatus, setCarsByStatus] = useState([]);
  const [loadingCarsByStatus, setLoadingCarsByStatus] = useState(false);
  const [transAmountLimits, setTransAmountLimits] = useState({});

  // State for UI
  const [errors, setErrors] = useState({});
  const [errorOpen, setErrorOpen] = useState(false);
  const [successOpen, setSuccessOpen] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submissionResults, setSubmissionResults] = useState(null);

  // Fetch data on component mount
  useEffect(() => {
    const fetchData = async () => {
      const token = localStorage.getItem('authToken') || 'default-token';
      try {
        const [carPlatesResponse, typeResponse, statusResponse, limitsResponse] = await Promise.all([
          axios.get(`${rootUrl}/cars/plates`, { headers: { 'Authorization': `Bearer ${token}` } }),
          axios.get(`${rootUrl}/payments/types/car`, { headers: { 'Authorization': `Bearer ${token}` } }),
          axios.get(`${rootUrl}/cars/status`, { headers: { 'Authorization': `Bearer ${token}` } }),
          axios.get(`${rootUrl}/system/car_trans_amount_limits`, { headers: { 'Authorization': `Bearer ${token}` } })
        ]);

        setCarPlates(carPlatesResponse.data);
        const typesData = typeResponse.data.map(item => ({
          type: item.type,
          short_desc: item.short_desc,
          long_desc: item.long_desc,
          def: item.def
        }));
        setTypes(typesData);

        // Set car statuses
        const statusData = statusResponse.data.data || [];
        setCarStatuses(statusData);

        const limitsValue = limitsResponse?.data?.value;
        if (limitsValue) {
          try {
            const parsed = JSON.parse(limitsValue);
            const normalized = Object.entries(parsed || {}).reduce((acc, [key, value]) => {
              const num = parseFloat(value);
              if (!Number.isNaN(num)) {
                acc[key] = num;
              }
              return acc;
            }, {});
            setTransAmountLimits(normalized);
          } catch (err) {
            console.error('Error parsing car_trans_amount_limits:', err);
          }
        }

        // Set default transaction type
        const defaultType = typesData.find(type => type.def === true);
        if (defaultType) {
          setFormData(prev => ({
            ...prev,
            car_trans_type: defaultType.type
          }));
        }
      } catch (error) {
        console.error("Error fetching data: ", error);
        setErrors({ api: 'Failed to load data. Please refresh the page.' });
        setErrorOpen(true);
      }
    };

    fetchData();
  }, []);

  // Update input_by when user changes
  useEffect(() => {
    if (user && user.username) {
      setFormData(prev => ({
        ...prev,
        input_by: user.username
      }));
    }
  }, [user]);

  // Update auto-generated fields
  const updateAutoFields = () => {
    const newDescription = generateDescription(selectedStatus, formData.car_trans_type, formData.date);
    const newReference = generateReference(formData.date, formData.car_trans_type, selectedStatus);

    setFormData(prev => ({
      ...prev,
      notes: newDescription,
      your_ref: newReference
    }));
  };

  // Handle form field changes
  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));

    // Clear field-specific errors
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }

    // Auto-update description and reference when key fields change
    if (field === 'car_trans_type' || field === 'date') {
      // Use setTimeout to ensure state is updated before generating new values
      setTimeout(() => {
        updateAutoFields();
      }, 0);
    }
  };

  // Handle car selection
  const handleCarSelection = (selectedOptions) => {
    setSelectedCars(selectedOptions || []);
  };

  // Load cars by status
  const loadCarsByStatus = async (statusId) => {
    if (!statusId) {
      setCarsByStatus([]);
      return;
    }

    setLoadingCarsByStatus(true);
    try {
      const token = localStorage.getItem('authToken') || 'default-token';
      const response = await axios.get(`${rootUrl}/cars/by-status/${statusId}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      const cars = response.data || [];
      const carPlates = cars.map(car => car.plate_no);
      setCarsByStatus(carPlates);
    } catch (error) {
      console.error("Error loading cars by status:", error);
      setErrors({ api: 'Failed to load cars by status. Please try again.' });
      setErrorOpen(true);
    } finally {
      setLoadingCarsByStatus(false);
    }
  };

  // Generate auto description
  const generateDescription = (status, transType, date) => {
    if (!status || !transType || !date) return '';

    const statusName = carStatuses.find(s => s.id === status)?.name || 'Unknown Status';
    const transTypeName = types.find(t => t.type === transType)?.long_desc || transType;
    const formattedDate = new Date(date).toLocaleDateString('en-KE', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });

    return `${transTypeName} for ${statusName} cars - ${formattedDate}`;
  };

  // Generate auto reference
  const generateReference = (date, transType, status) => {
    if (!date || !transType || !status) return '';

    const dateStr = new Date(date).toISOString().slice(0, 10).replace(/-/g, '');
    const statusCode = carStatuses.find(s => s.id === status)?.code || 'UNK';
    const transCode = transType.substring(0, 3).toUpperCase();

    return `${dateStr}-${transCode}-${statusCode}`;
  };

  // Handle status selection
  const handleStatusChange = (selectedOption) => {
    const statusId = selectedOption ? selectedOption.value : '';
    setSelectedStatus(statusId);
    loadCarsByStatus(statusId);

    // Set default payment type from status
    if (selectedOption) {
      const selectedStatus = carStatuses.find(status => status.id === statusId);
      if (selectedStatus && selectedStatus.def_pay_type) {
        handleInputChange('car_trans_type', selectedStatus.def_pay_type);
      }
    }

    // Auto-generate description and reference
    updateAutoFields();
  };

  // Add car to selection
  const addCar = (carPlate) => {
    if (!selectedCars.find(car => car.value === carPlate)) {
      const newCar = { value: carPlate, label: carPlate };
      setSelectedCars(prev => [...prev, newCar]);
    }
  };

  // Add all cars from status to selection
  const addAllCarsFromStatus = () => {
    const newCars = carsByStatus
      .filter(plate => !selectedCars.find(car => car.value === plate))
      .map(plate => ({ value: plate, label: plate }));
    setSelectedCars(prev => [...prev, ...newCars]);
  };

  // Remove car from selection
  const removeCar = (carPlate) => {
    setSelectedCars(prev => prev.filter(car => car.value !== carPlate));
  };

  // Handle paste event in notes field (similar to single transaction)
  const handleNotesPaste = (e) => {
    const pastedText = e.clipboardData.getData('text');
    const words = pastedText.trim().split(/\s+/);

    // Extract reference code (first word if 10 characters)
    if (words.length > 0) {
      const firstWord = words[0];
      if (firstWord.length === 10) {
        handleInputChange('your_ref', firstWord.toUpperCase());
      }
    }

    // Extract amount from the message
    const amountMatch = pastedText.match(/Ksh([\d,]+\.?\d*)/);
    if (amountMatch) {
      const amount = parseFloat(amountMatch[1].replace(/,/g, ''));
      if (!isNaN(amount)) {
        handleInputChange('amount', amount.toString());
      }
    }

    // Extract date from the message (format: 22/7/25)
    const dateMatch = pastedText.match(/(\d{1,2})\/(\d{1,2})\/(\d{2})/);
    if (dateMatch) {
      const day = parseInt(dateMatch[1]);
      const month = parseInt(dateMatch[2]);
      const year = parseInt(dateMatch[3]) + 2000;

      const dateString = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`;
      handleInputChange('date', dateString);
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};

    if (!formData.car_trans_type) newErrors.car_trans_type = 'Please select transaction type';
    if (!formData.date) newErrors.date = 'Please select date';
    if (!formData.amount) newErrors.amount = 'Please enter amount';
    if (!formData.your_ref) newErrors.your_ref = 'Please enter reference code';
    if (!formData.notes) newErrors.notes = 'Please enter notes';
    if (selectedCars.length === 0) newErrors.cars = 'Please select at least one car';

    // Validate amount is a positive number
    const amountValue = parseFloat(formData.amount);
    if (formData.amount && (Number.isNaN(amountValue) || amountValue <= 0)) {
      newErrors.amount = 'Amount must be a positive number';
    }

    if (!Number.isNaN(amountValue)) {
      const limit = transAmountLimits[formData.car_trans_type];
      if (limit !== undefined && limit !== null && amountValue > limit) {
        newErrors.amount = `Amount exceeds maximum of ${limit} for ${formData.car_trans_type}`;
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    setSubmissionResults(null);

    try {
      // Prepare transactions array
      const transactions = selectedCars.map(car => ({
        plate_no: car.value,
        amount: parseFloat(formData.amount),
        car_trans_type: formData.car_trans_type,
        date: formData.date,
        your_ref: formData.your_ref,
        notes: formData.notes
      }));

      const payload = {
        transactions,
        input_by: formData.input_by
      };

      const response = await axios.post(`${rootUrl}/cars/bulk-trans`, payload);

      if (response.status === 200 || response.status === 207) {
        setSubmissionResults(response.data.results);
        const msg = response.data.message;
        setSuccessMessage(msg);
        setSuccessOpen(true);

        // Auto-clear after 8 seconds
        setTimeout(() => setSuccessOpen(false), 8000);

        // Reset form if all transactions were successful
        if (response.data.results.success) {
          setFormData({
            car_trans_type: '',
            date: new Date().toISOString().split('T')[0],
            amount: '',
            notes: '',
            your_ref: '',
            input_by: user?.username || ''
          });
          setSelectedCars([]);
        }
      }

    } catch (error) {
      console.error("Error submitting bulk transactions: ", error);
      if (error.response && error.response.data) {
        const errorMessage = error.response.data.error || 'An error occurred while posting transactions';
        setErrors({ api: errorMessage });
        setErrorOpen(true);
      } else {
        setErrors({ api: 'Network error. Please check your connection and try again.' });
        setErrorOpen(true);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // Reset form
  const handleReset = () => {
    setFormData({
      car_trans_type: '',
      date: new Date().toISOString().split('T')[0],
      amount: '',
      notes: '',
      your_ref: '',
      input_by: user?.username || ''
    });
    setSelectedCars([]);
    setSelectedStatus('');
    setCarsByStatus([]);
    setErrors({});
    setSubmissionResults(null);
  };

  const isSubmitDisabled = !formData.car_trans_type || !formData.date || !formData.amount ||
    !formData.your_ref || !formData.notes || selectedCars.length === 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA] p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">Bulk Transaction Posting</h1>
          <div className="w-24 h-1 bg-[#FF5733] mx-auto rounded-full"></div>
          <p className="text-gray-600 mt-4">Post the same transaction to multiple cars at once</p>
        </div>

        {/* Main Form Card */}
        <Card className="bg-white rounded-2xl shadow-xl p-8 mb-6">
          <Typography variant="h5" color="blue-gray" className="font-semibold text-[#2a2e30] mb-6 flex items-center">
            <FaMoneyCheckAlt className="mr-3 text-[#FF5733]" />
            Transaction Details
          </Typography>

          {/* Auto-generation Info */}
          {selectedStatus && formData.car_trans_type && formData.date && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-xl">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-blue-700">
                    <strong>Auto-generation active:</strong> Description and reference are automatically generated based on your selections. You can still edit them manually if needed.
                  </p>
                </div>
              </div>
            </div>
          )}

          {errors.api && <Alerty open={errorOpen} setOpen={setErrorOpen} color="red" message={errors.api} variant="solid" />}
          {successMessage && <Alerty open={successOpen} setOpen={setSuccessOpen} color="green" message={successMessage} variant="solid" />}

          <form onSubmit={handleSubmit}>
            {/* Form Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column */}
              <div className="space-y-6">
                {/* Transaction Date */}
                <div className="space-y-2">
                  <label className="flex items-center text-sm font-semibold text-[#2a2e30]">
                    <FaCalendarAlt className="mr-2 text-[#FF5733]" />
                    Transaction Date
                  </label>
                  <input
                    type="date"
                    value={formData.date}
                    onChange={(e) => handleInputChange('date', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FF5733] focus:border-transparent transition-all duration-200 bg-white"
                  />
                  {errors.date && <p className="text-red-500 text-sm">{errors.date}</p>}
                </div>

                {/* Transaction Type */}
                <div className="space-y-2">
                  <label className="flex items-center text-sm font-semibold text-[#2a2e30]">
                    <FaFileAlt className="mr-2 text-[#FF5733]" />
                    Transaction Type
                  </label>
                  <Select
                    value={
                      formData.car_trans_type
                        ? { value: formData.car_trans_type, label: types.find((type) => type.type === formData.car_trans_type)?.long_desc || '' }
                        : null
                    }
                    onChange={(selectedOption) => handleInputChange('car_trans_type', selectedOption ? selectedOption.value : '')}
                    options={types.map((type) => ({
                      value: type.type,
                      label: type.long_desc,
                    }))}
                    placeholder="Choose transaction type"
                    className="w-full"
                    classNamePrefix="custom-select"
                    styles={{
                      control: (provided) => ({
                        ...provided,
                        border: '1px solid #d1d5db',
                        borderRadius: '12px',
                        padding: '4px',
                        '&:hover': { borderColor: '#FF5733' }
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isSelected ? '#FF5733' : state.isFocused ? '#fff8f5' : 'white',
                        color: state.isSelected ? 'white' : '#2a2e30'
                      })
                    }}
                  />
                  {errors.car_trans_type && <p className="text-red-500 text-sm">{errors.car_trans_type}</p>}
                </div>

                {/* Reference */}
                <div className="space-y-2">
                  <label className="flex items-center text-sm font-semibold text-[#2a2e30]">
                    <FaTag className="mr-2 text-[#FF5733]" />
                    Reference Code
                    {selectedStatus && formData.car_trans_type && formData.date && (
                      <span className="ml-2 text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">
                        Auto-generated
                      </span>
                    )}
                  </label>
                  <input
                    type="text"
                    value={formData.your_ref}
                    onChange={(e) => handleInputChange('your_ref', e.target.value.toUpperCase())}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FF5733] focus:border-transparent transition-all duration-200 bg-white"
                    placeholder="Enter reference code"
                  />
                  {errors.your_ref && <p className="text-red-500 text-sm">{errors.your_ref}</p>}
                </div>
              </div>

              {/* Right Column */}
              <div className="space-y-6">
                {/* Amount */}
                <div className="space-y-2">
                  <label className="flex items-center text-sm font-semibold text-[#2a2e30]">
                    <FaMoneyCheckAlt className="mr-2 text-[#FF5733]" />
                    Amount (KES)
                  </label>
                  <input
                    type="number"
                    value={formData.amount}
                    onChange={(e) => handleInputChange('amount', e.target.value)}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FF5733] focus:border-transparent transition-all duration-200 bg-white text-right font-mono"
                    placeholder="0.00"
                    min="0"
                    step="0.01"
                  />
                  {errors.amount && <p className="text-red-500 text-sm">{errors.amount}</p>}
                </div>

                {/* Notes */}
                <div className="space-y-2">
                  <label className="flex items-center text-sm font-semibold text-[#2a2e30]">
                    <FaFileAlt className="mr-2 text-[#FF5733]" />
                    Transaction Notes
                    {selectedStatus && formData.car_trans_type && formData.date && (
                      <span className="ml-2 text-xs text-green-600 bg-green-100 px-2 py-1 rounded-full">
                        Auto-generated
                      </span>
                    )}
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => handleInputChange('notes', e.target.value)}
                    onPaste={handleNotesPaste}
                    className="w-full px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#FF5733] focus:border-transparent transition-all duration-200 bg-white resize-none h-32"
                    placeholder="Enter transaction notes (paste M-Pesa message to auto-fill reference, amount, and date)"
                  />
                  {errors.notes && <p className="text-red-500 text-sm">{errors.notes}</p>}
                </div>
              </div>
            </div>

            {/* Car Selection Section */}
            <div className="mt-8 pt-6 border-t border-gray-200">
              <Typography variant="h6" color="blue-gray" className="font-semibold text-[#2a2e30] mb-4 flex items-center">
                <FaCar className="mr-3 text-[#FF5733]" />
                Select Cars ({selectedCars.length} selected)
              </Typography>

              {/* Status Filter Section */}
              <div className="mb-6 p-4 bg-gray-50 rounded-xl border border-gray-200">
                <Typography variant="h6" color="blue-gray" className="font-semibold text-[#2a2e30] mb-3 flex items-center">
                  <FaFilter className="mr-2 text-[#FF5733]" />
                  Filter by Status (Optional)
                </Typography>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Status Selection */}
                  <div>
                    <label className="flex items-center text-sm font-semibold text-[#2a2e30] mb-2">
                      <FaUsers className="mr-2 text-[#FF5733]" />
                      Car Status
                    </label>
                    <Select
                      value={
                        selectedStatus
                          ? { value: selectedStatus, label: carStatuses.find((status) => status.id === selectedStatus)?.name || 'Unknown Status' }
                          : null
                      }
                      onChange={handleStatusChange}
                      options={carStatuses.map((status) => ({
                        value: status.id,
                        label: `${status.name} (${status.code})`,
                      }))}
                      placeholder="Select status to filter cars..."
                      className="w-full"
                      classNamePrefix="custom-select"
                      isClearable
                      styles={{
                        control: (provided) => ({
                          ...provided,
                          border: '1px solid #d1d5db',
                          borderRadius: '12px',
                          padding: '4px',
                          '&:hover': { borderColor: '#FF5733' }
                        }),
                        option: (provided, state) => ({
                          ...provided,
                          backgroundColor: state.isSelected ? '#FF5733' : state.isFocused ? '#fff8f5' : 'white',
                          color: state.isSelected ? 'white' : '#2a2e30'
                        })
                      }}
                    />
                  </div>

                  {/* Cars by Status Display */}
                  {carsByStatus.length > 0 && (
                    <div>
                      <label className="flex items-center text-sm font-semibold text-[#2a2e30] mb-2">
                        <FaCar className="mr-2 text-[#FF5733]" />
                        Cars in Selected Status ({carsByStatus.length})
                      </label>
                      <div className="flex flex-wrap gap-2 mb-3">
                        {carsByStatus.map((plate) => (
                          <Chip
                            key={plate}
                            value={plate}
                            className={`cursor-pointer transition-all duration-200 ${selectedCars.find(car => car.value === plate)
                                ? 'bg-[#FF5733] text-white'
                                : 'bg-white text-[#2a2e30] border border-gray-300 hover:bg-[#FF5733] hover:text-white'
                              }`}
                            onClick={() => {
                              if (selectedCars.find(car => car.value === plate)) {
                                removeCar(plate);
                              } else {
                                addCar(plate);
                              }
                            }}
                          />
                        ))}
                      </div>
                      <Button
                        size="sm"
                        variant="outlined"
                        color="blue"
                        className="text-xs"
                        onClick={addAllCarsFromStatus}
                        disabled={loadingCarsByStatus}
                      >
                        <FaPlus className="mr-1" />
                        Add All ({carsByStatus.length})
                      </Button>
                    </div>
                  )}

                  {loadingCarsByStatus && (
                    <div className="flex items-center justify-center p-4">
                      <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-[#FF5733]"></div>
                      <span className="ml-2 text-sm text-gray-600">Loading cars...</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Manual Car Selection */}
              <div className="mb-4">
                <label className="flex items-center text-sm font-semibold text-[#2a2e30] mb-2">
                  <FaCar className="mr-2 text-[#FF5733]" />
                  Manual Car Selection
                </label>
                <Select
                  isMulti
                  value={selectedCars}
                  onChange={handleCarSelection}
                  options={carPlates.map((plate) => ({
                    value: plate,
                    label: plate,
                  }))}
                  placeholder="Select cars to post transaction to..."
                  className="w-full"
                  classNamePrefix="custom-select"
                  styles={{
                    control: (provided) => ({
                      ...provided,
                      border: '1px solid #d1d5db',
                      borderRadius: '12px',
                      padding: '4px',
                      '&:hover': { borderColor: '#FF5733' }
                    }),
                    option: (provided, state) => ({
                      ...provided,
                      backgroundColor: state.isSelected ? '#FF5733' : state.isFocused ? '#fff8f5' : 'white',
                      color: state.isSelected ? 'white' : '#2a2e30'
                    })
                  }}
                />
                {errors.cars && <p className="text-red-500 text-sm mt-2">{errors.cars}</p>}
              </div>

              {/* Selected Cars Display */}
              {selectedCars.length > 0 && (
                <div>
                  <label className="flex items-center text-sm font-semibold text-[#2a2e30] mb-2">
                    <FaCheck className="mr-2 text-green-600" />
                    Selected Cars ({selectedCars.length})
                  </label>
                  <div className="flex flex-wrap gap-2">
                    {selectedCars.map((car) => (
                      <Chip
                        key={car.value}
                        value={car.value}
                        onClose={() => removeCar(car.value)}
                        className="bg-[#FF5733] text-white"
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-gray-200">
              <Button
                variant="outlined"
                color="gray"
                className="px-6 py-3"
                onClick={handleReset}
                disabled={isSubmitting}
              >
                <FaTimes className="mr-2" />
                Reset
              </Button>

              <Button
                type="submit"
                className={`px-8 py-3 ${isSubmitDisabled || isSubmitting
                  ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                  : 'bg-[#FF5733] hover:bg-[#e64a2b]'
                  }`}
                disabled={isSubmitDisabled || isSubmitting}
              >
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin -ml-1 mr-3 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    Processing...
                  </>
                ) : (
                  <>
                    <FaCheck className="mr-2" />
                    Post to {selectedCars.length} Car{selectedCars.length !== 1 ? 's' : ''}
                  </>
                )}
              </Button>
            </div>
          </form>
        </Card>

        {/* Submission Results */}
        {submissionResults && (
          <Card className="bg-white rounded-2xl shadow-xl p-8">
            <Typography variant="h5" color="blue-gray" className="font-semibold text-[#2a2e30] mb-6">
              Submission Results
            </Typography>

            {/* Summary */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
              <div className="bg-blue-50 p-4 rounded-xl text-center">
                <div className="text-2xl font-bold text-blue-600">{submissionResults.total}</div>
                <div className="text-sm text-blue-800">Total</div>
              </div>
              <div className="bg-green-50 p-4 rounded-xl text-center">
                <div className="text-2xl font-bold text-green-600">{submissionResults.successful}</div>
                <div className="text-sm text-green-800">Successful</div>
              </div>
              <div className="bg-red-50 p-4 rounded-xl text-center">
                <div className="text-2xl font-bold text-red-600">{submissionResults.failed}</div>
                <div className="text-sm text-red-800">Failed</div>
              </div>
              <div className="bg-gray-50 p-4 rounded-xl text-center">
                <div className="text-2xl font-bold text-gray-600">
                  {submissionResults.total > 0 ? Math.round((submissionResults.successful / submissionResults.total) * 100) : 0}%
                </div>
                <div className="text-sm text-gray-800">Success Rate</div>
              </div>
            </div>

            {/* Successful Transactions */}
            {submissionResults.successful_transactions.length > 0 && (
              <div className="mb-6">
                <Typography variant="h6" color="green" className="font-semibold mb-3">
                  ✅ Successful Transactions
                </Typography>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {submissionResults.successful_transactions.map((transaction, index) => (
                    <div key={index} className="bg-green-50 p-3 rounded-lg border border-green-200">
                      <div className="font-medium text-green-800">{transaction.plate_no}</div>
                      <div className="text-sm text-green-600">Doc: {transaction.doc_no}</div>
                      <div className="text-sm text-green-600">Ref: {transaction.your_ref}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Failed Transactions */}
            {submissionResults.failed_transactions.length > 0 && (
              <div>
                <Typography variant="h6" color="red" className="font-semibold mb-3">
                  ❌ Failed Transactions
                </Typography>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {submissionResults.failed_transactions.map((transaction, index) => (
                    <div key={index} className="bg-red-50 p-3 rounded-lg border border-red-200">
                      <div className="font-medium text-red-800">{transaction.transaction.plate_no}</div>
                      <div className="text-sm text-red-600">Error: {transaction.error}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Card>
        )}
      </div>
    </div>
  );
};

export default BulkPosting;
